import { Box, Button, Chip, IconButton, Snackbar, TextField, Typography, useTheme } from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { tokens } from '../../theme';
import Header from '../../components/Header';
import ConfirmDeleteDialog from '../../components/ConfirmDeleteDialog';
import DeleteIcon from '@mui/icons-material/Delete';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import VisibilityIcon from '@mui/icons-material/Visibility';

const ReportsByUser = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const [openToast, setOpenToast] = useState(false);
  const [toastMessage, setToastmessage] = useState('');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [sortModel, setSortModel] = useState([{ field: 'id', sort: 'asc' }]);
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [idToDelete, setIdToDelete] = useState(null);

  const token = localStorage.getItem('token');

  // Debounce the search box so typing doesn't fire a request per keystroke.
  useEffect(() => {
    const t = setTimeout(() => {
      setSearch(searchInput);
      setPage(1);
    }, 300);
    return () => clearTimeout(t);
  }, [searchInput]);

  const getData = async () => {
    setLoading(true);

    const sortField = sortModel[0]?.field || 'id';
    const sortOrder = sortModel[0]?.sort || 'asc';

    try {
      const params = new URLSearchParams();
      params.set('page', String(page));
      params.set('limit', String(pageSize));
      params.set('sortBy', sortField);
      params.set('sortOrder', sortOrder);
      if (search) params.set('search', search);

      const res = await fetch(`${process.env.REACT_APP_APILINK}/getallpassengers?${params.toString()}`, {
        headers: { token },
      });
      const data = await res.json();
      if (data.success) {
        setRows(data.passengers);
        setTotalCount(data?.pagination?.total || data.passengers?.length || 0);
      }
    } catch (e) {
      console.error('ReportsByUser fetch error', e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getData();
    // eslint-disable-next-line
  }, [page, pageSize, JSON.stringify(sortModel), search]);

  const handleClearSearch = () => {
    setSearchInput('');
    setSearch('');
    setPage(1);
  };

  const handleDelete = async (id) => {
    const res = await fetch(`${process.env.REACT_APP_APILINK}/passengers/${id}`, {
      headers: { token },
      method: 'DELETE',
    });
    const data = await res.json();
    if (data.message) {
      setOpenToast(true);
      setToastmessage(data.message);
      if (data.success) getData();
    }
  };

  const openDeleteDialog = (id) => {
    setIdToDelete(id);
    setDeleteDialogOpen(true);
  };

  const closeDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setIdToDelete(null);
  };

  const confirmDelete = async () => {
    if (idToDelete == null) return;
    await handleDelete(idToDelete);
    closeDeleteDialog();
  };

  const columns = [
    { field: 'id', headerName: 'Id', hide: true, filterable: false, sortable: false },
    { field: 'name', headerName: 'Name', flex: 1, minWidth: 120, cellClassName: 'name-column--cell' },
    { field: 'surname', headerName: 'Surname', flex: 1, minWidth: 120, cellClassName: 'name-column--cell' },
    { field: 'email', headerName: 'Email', flex: 1, minWidth: 160 },
    { field: 'phone', headerName: 'Phone Number', flex: 1, minWidth: 120 },
    { field: 'address', headerName: 'Address', flex: 1, minWidth: 160 },
    {
      field: 'View',
      filterable: false,
      sortable: false,
      renderCell: (cellValues) => (
        <Link to={`/reports-by-user/${cellValues.row.id}`}>
          <Button
            sx={{
              backgroundColor: colors.blueAccent[500],
              color: colors.grey[100],
              ':hover': { backgroundColor: colors.blueAccent[600] },
            }}
            variant="contained"
          >
            <VisibilityIcon />
          </Button>
        </Link>
      ),
    },
    {
      field: 'Delete',
      filterable: false,
      sortable: false,
      renderCell: (cellValues) => (
        <Button
          sx={{
            backgroundColor: colors.redAccent[500],
            color: colors.grey[100],
            ':hover': { backgroundColor: colors.redAccent[600] },
          }}
          variant="contained"
          onClick={() => openDeleteDialog(cellValues.row.id)}
        >
          <DeleteIcon />
        </Button>
      ),
    },
  ];

  const handleCloseToast = (event, reason) => {
    if (reason === 'clickaway') return;
    setOpenToast(false);
  };

  const action = (
    <>
      <IconButton size="small" aria-label="close" color="inherit" onClick={handleCloseToast}>
        <CloseIcon fontSize="small" />
      </IconButton>
    </>
  );

  const handleSortModelChange = (newSortModel) => {
    setSortModel(newSortModel);
    setPage(1);
  };

  return (
    <Box m="20px">
      <Box display="flex" justifyContent="space-between" alignItems="center" gap="10px" flexWrap="wrap">
        <Header title="REPORT BY USER" subtitle="View passenger-wise bookings and details" />
      </Box>

      <Box display="flex" gap="10px" flexWrap="wrap" alignItems="center" m="10px 0 0 0">
        <TextField
          size="small"
          placeholder="Search by name, surname, email, phone, address..."
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          InputProps={{
            startAdornment: <SearchIcon sx={{ mr: 1, color: colors.grey[300] }} />,
          }}
          sx={{ minWidth: '320px', backgroundColor: colors.primary[400] }}
        />
      </Box>

      {search && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mt: 1, mb: 1 }}>
          <Typography variant="body2" sx={{ color: colors.grey[300], fontWeight: 500 }}>
            Active filters:
          </Typography>
          <Chip
            label={`Search: ${search}`}
            size="small"
            onDelete={handleClearSearch}
            sx={{ backgroundColor: colors.blueAccent[700], color: colors.grey[100], '& .MuiChip-deleteIcon': { color: colors.grey[300] } }}
          />
          {!loading && (
            <Typography variant="body2" sx={{ color: colors.greenAccent[400], ml: 1 }}>
              {totalCount} result{totalCount !== 1 ? 's' : ''}
            </Typography>
          )}
          <Typography
            variant="body2"
            onClick={handleClearSearch}
            sx={{
              color: colors.redAccent[500],
              cursor: 'pointer',
              ml: 1,
              '&:hover': { textDecoration: 'underline' },
            }}
          >
            Clear all
          </Typography>
        </Box>
      )}

      <Box
        m="10px 0 0 0"
        height="667px"
        sx={{
          '& .MuiDataGrid-root': { border: 'none' },
          '& .MuiDataGrid-cell': { borderBottom: 'none' },
          '& .name-column--cell': { color: colors.greenAccent[300] },
          '& .MuiDataGrid-columnHeaders': { backgroundColor: colors.blueAccent[700], borderBottom: 'none' },
          '& .MuiDataGrid-virtualScroller': { backgroundColor: colors.primary[400] },
          '& .MuiDataGrid-footerContainer': { borderTop: 'none', backgroundColor: colors.blueAccent[700] },
          '& .MuiCheckbox-root': { color: `${colors.greenAccent[200]} !important` },
          '& .MuiDataGrid-toolbarContainer .MuiButton-text': { color: `${colors.grey[100]} !important`, fontSize: 14 },
        }}
      >
        <DataGrid
          rows={rows}
          columns={columns}
          getRowId={(row) => row.id}
          disableColumnFilter
          page={page - 1}
          pageSize={pageSize}
          sortingOrder={['asc', 'desc']}
          sortModel={sortModel}
          onSortModelChange={handleSortModelChange}
          onPageChange={(newPage) => setPage(newPage + 1)}
          onPageSizeChange={(newPageSize) => setPageSize(newPageSize)}
          paginationMode="server"
          rowCount={totalCount}
          loading={loading}
        />
      </Box>

      <ConfirmDeleteDialog
        open={deleteDialogOpen}
        onClose={closeDeleteDialog}
        onConfirm={confirmDelete}
        title="Delete passenger?"
        message="Are you sure you want to delete this passenger?"
        colors={colors}
      />

      <Snackbar
        open={openToast}
        autoHideDuration={5000}
        onClose={handleCloseToast}
        message={toastMessage}
        action={action}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      />
    </Box>
  );
};

export default ReportsByUser;
